import get from 'lodash/get';

import type { SdkHelpers } from './types';

/**
 * Builds `Sdk.helpers` — the page input + route parameter readers — over the
 * page's injected input data.
 */
export function createHelpers(data: Record<string, unknown>): SdkHelpers {
  return {
    getInput: (key: string, defaultValue: unknown = null) => get(data, key, defaultValue),
    useInput: (key: string, defaultValue: unknown = null) => get(data, key, defaultValue),
    // Raw route parameter values from the current request URL, keyed by param
    // name. Always present (the backend reserves the `params` key), so this
    // returns an empty object rather than null when the page has no params.
    getRouteParams: (): Record<string, unknown> => {
      const params = get(data, 'params', {});
      return params && typeof params === 'object' && !Array.isArray(params)
        ? (params as Record<string, unknown>)
        : {};
    },
    // A single raw route parameter value, e.g. the `{member}` URL segment as a
    // string. Available even when the param has no route binding.
    getRouteParam: (key: string, defaultValue: unknown = null) =>
      get(data, ['params', key], defaultValue),
    // The hydrated record bound to a route param via a route binding, or
    // `undefined` when that param has no binding configured. This is the
    // resolved resource that `getRouteParam` only exposes the raw value of.
    getRouteBinding: (key: string, defaultValue?: unknown) => get(data, key, defaultValue)
  };
}
